import { useState, useEffect, useRef } from 'react'
import {
  CAPABILITY_GROUPS,
  PERMISSIONS,
  PERMISSION_CAPABILITY_GROUP,
} from '../constants'
import { decodePermissions } from '../utils'
import { PermissionCard } from './PermissionCard'

interface PermissionSelectorProps {
  value: bigint
  onChange: (permissions: bigint) => void
  initialPreset?: string
  existingPermissions?: bigint | null
  hidePresets?: boolean
  defaultMode?: 'preset' | 'custom'
  allowedCallsCount?: number
  allowedDataKeysCount?: number
}

const PERMISSION_ORDER: string[] = [
  'STATICCALL',
  'SUPER_STATICCALL',
  'SIGN',
  'ENCRYPT',
  'DECRYPT',
  'EXECUTE_RELAY_CALL',
  'SETDATA',
  'SUPER_SETDATA',
  'CALL',
  'SUPER_CALL',
  'DELEGATECALL',
  'SUPER_DELEGATECALL',
  'DEPLOY',
  'TRANSFERVALUE',
  'SUPER_TRANSFERVALUE',
  'ADDCONTROLLER',
  'EDITPERMISSIONS',
  'CHANGEOWNER',
  'ADDEXTENSIONS',
  'CHANGEEXTENSIONS',
  'ADDUNIVERSALRECEIVERDELEGATE',
  'CHANGEUNIVERSALRECEIVERDELEGATE',
  'REENTRANCY',
]

export function PermissionSelector({
  value,
  onChange,
  existingPermissions,
  allowedCallsCount = 0,
  allowedDataKeysCount = 0,
}: PermissionSelectorProps) {
  const [customPermissions, setCustomPermissions] = useState<Set<string>>(new Set())
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => new Set(CAPABILITY_GROUPS.map((g) => g.key)))
  const hasInitializedExisting = useRef(false)

  const toggleGroup = (groupKey: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(groupKey)) next.delete(groupKey)
      else next.add(groupKey)
      return next
    })
  }

  // Initialize from existing on-chain permissions when present
  useEffect(() => {
    if (existingPermissions !== null && existingPermissions !== undefined && existingPermissions > 0n && !hasInitializedExisting.current) {
      hasInitializedExisting.current = true
      const decoded = decodePermissions(existingPermissions.toString())
      setCustomPermissions(new Set(decoded))
    }
  }, [existingPermissions])

  useEffect(() => {
    if (!existingPermissions) {
      hasInitializedExisting.current = false
    }
  }, [existingPermissions])

  // Sync value -> customPermissions when value changes externally (e.g. template install)
  useEffect(() => {
    if (value !== undefined && value !== null && value > 0n) {
      const decoded = decodePermissions(value.toString())
      setCustomPermissions(new Set(decoded))
    }
  }, [value])

  const handleCustomToggle = (permName: string) => {
    const newPermissions = new Set(customPermissions)
    if (newPermissions.has(permName)) {
      newPermissions.delete(permName)
    } else {
      newPermissions.add(permName)
    }
    setCustomPermissions(newPermissions)
    let combined = 0n
    for (const perm of newPermissions) {
      combined |= BigInt(PERMISSIONS[perm as keyof typeof PERMISSIONS])
    }
    onChange(combined)
  }

  return (
    <div className="card-hub">
      <div className="space-y-6">
        {CAPABILITY_GROUPS.map((group) => {
          const allKeys = Object.keys(PERMISSIONS).filter((k) => k !== 'ALL_PERMISSIONS')
          const keysInGroup = allKeys
            .filter((k) => (PERMISSION_CAPABILITY_GROUP[k] ?? 'identitySigning') === group.key)
            .sort((a, b) => PERMISSION_ORDER.indexOf(a) - PERMISSION_ORDER.indexOf(b))
          if (keysInGroup.length === 0) return null

          const isCollapsed = collapsedGroups.has(group.key)
          const selectedCount = keysInGroup.filter((k) => customPermissions.has(k)).length

          return (
            <div key={group.key}>
              <button
                type="button"
                onClick={() => toggleGroup(group.key)}
                className="flex items-center justify-between gap-3 w-full mb-2 text-left group"
              >
                <span className="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-gray-700 dark:group-hover:text-gray-200 min-w-0">
                  {group.label}
                </span>
                <span className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-[11px] text-gray-500 dark:text-gray-400">
                    {selectedCount} selected
                  </span>
                  <svg
                    className={`w-4 h-4 text-gray-500 transition-transform flex-shrink-0 ${isCollapsed ? '' : 'rotate-180'}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </span>
              </button>
              {!isCollapsed && (
                <div className="space-y-4">
                  {keysInGroup.map((permKey) => (
                    <PermissionCard
                      key={permKey}
                      permissionKey={permKey}
                      capabilityGroupKey={group.key}
                      checked={customPermissions.has(permKey)}
                      onToggle={handleCustomToggle}
                      allowedCallsCount={allowedCallsCount}
                      allowedDataKeysCount={allowedDataKeysCount}
                    />
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
